import React, { useState } from 'react';
import { Play, Square, RefreshCw, Camera, ExternalLink } from 'lucide-react';

interface SessionToolbarProps {
  isSessionActive: boolean;
  currentUrl: string | null;
  isLoading: boolean;
  onStart: () => void;
  onStop: () => void;
  onNavigate: (url: string) => void;
  onScreenshot: () => void;
  onRefresh: () => void;
  onOpenExternal: () => void;
}

export function SessionToolbar({
  isSessionActive,
  currentUrl,
  isLoading,
  onStart,
  onStop,
  onNavigate,
  onScreenshot,
  onRefresh,
  onOpenExternal,
}: SessionToolbarProps) {
  const [urlInput, setUrlInput] = useState('');

  const handleNavigate = (e: React.FormEvent) => {
    e.preventDefault();
    if (urlInput.trim()) {
      onNavigate(urlInput.trim());
    }
  };

  const handleRefresh = () => {
    if (currentUrl) {
      onNavigate(currentUrl);
    } else {
      onRefresh();
    }
  };

  return (
    <div className="flex items-center gap-2 p-3 bg-gray-900 border-b border-gray-700">
      <div className="flex items-center gap-2">
        {!isSessionActive ? (
          <button
            onClick={onStart}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-md transition-colors"
            title="Start Browser Session"
          >
            <Play size={16} />
            <span>Start</span>
          </button>
        ) : (
          <button
            onClick={onStop}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white rounded-md transition-colors"
            title="Stop Browser Session"
          >
            <Square size={16} />
            <span>Stop</span>
          </button>
        )}
      </div>

      {isSessionActive && (
        <>
          <div className="h-6 w-px bg-gray-700" />
          
          <form onSubmit={handleNavigate} className="flex-1 flex items-center gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder={currentUrl || 'Enter URL...'}
              disabled={isLoading}
              className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isLoading || !urlInput.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-md transition-colors"
            >
              Go
            </button>
          </form>

          <div className="h-6 w-px bg-gray-700" />

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 hover:bg-gray-800 disabled:opacity-50 text-gray-300 rounded-md transition-colors"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>

            <button
              onClick={onScreenshot}
              disabled={isLoading}
              className="p-2 hover:bg-gray-800 disabled:opacity-50 text-gray-300 rounded-md transition-colors"
              title="Take Screenshot"
            >
              <Camera size={18} />
            </button>

            <button
              onClick={onOpenExternal}
              disabled={isLoading || !currentUrl}
              className="p-2 hover:bg-gray-800 disabled:opacity-50 text-gray-300 rounded-md transition-colors"
              title="Open in New Tab"
            >
              <ExternalLink size={18} />
            </button>
          </div>
        </>
      )}

      {isLoading && (
        <div className="ml-auto">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-500 border-t-blue-500" />
        </div>
      )}
    </div>
  );
}
